import { useEffect, useState } from 'react';
import { applyTheme, readTheme, resolved, type Theme } from '../lib/theme';

const ORDER: Theme[] = ['system', 'light', 'dark'];
const LABEL: Record<Theme, string> = { system: 'Auto', light: 'Light', dark: 'Dark' };
const GLYPH: Record<Theme, string> = { system: '◐', light: '☀', dark: '☾' };

/**
 * Cycles system → light → dark. One control rather than three, because this is
 * a setting people change twice a year and it should not cost a row of the
 * top bar to say so.
 *
 * The button announces the state it is IN, not the one it would move to —
 * a toggle labelled with its own destination is the classic ambiguity, and
 * on a three-way it is unreadable.
 */
export function ThemeToggle() {
  const [theme, setTheme] = useState<Theme>(() => readTheme());

  useEffect(() => { applyTheme(theme); }, [theme]);

  // On "system", the OS can change under us — at sunset, or when someone flips
  // it in settings. Without this the page keeps the theme it started with
  // until a reload, which reads as the toggle being broken.
  useEffect(() => {
    if (theme !== 'system') return;
    let mq: MediaQueryList;
    try { mq = window.matchMedia('(prefers-color-scheme: light)'); } catch { return; }
    const onChange = () => setTheme('system');
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [theme]);

  const next = ORDER[(ORDER.indexOf(theme) + 1) % ORDER.length]!;
  return (
    <button
      className="btn tiny themetog"
      title={theme === 'system'
        ? `Following your system — ${resolved(theme)} right now. Click for ${LABEL[next].toLowerCase()}.`
        : `${LABEL[theme]}. Click for ${LABEL[next].toLowerCase()}.`}
      aria-label={`Theme: ${LABEL[theme]}`}
      onClick={() => setTheme(next)}
    >
      <span aria-hidden="true">{GLYPH[theme]}</span> {LABEL[theme]}
    </button>
  );
}
